import { Show, onCleanup, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';

import { rectOf } from '../game/container';
import { useDrag } from './DragContext';
import { Icon } from './Icon';
import { rarityClass } from './stash';

/**
 * The item on the cursor, plus the window-level half of a drag.
 *
 * Three jobs, all of which have to be somewhere above the panels:
 *
 *  1. **The ghost.** Drawn at the pointer, at the same cell size as the grid it came
 *     from, so a 2 × 4 greatsword looks like the eight cells it will occupy.
 *  2. **Releasing over nothing.** A grid cell handles its own `pointerup`; anything
 *     else — the world, a panel's chrome, off the window — has to cancel, or the item
 *     stays glued to the cursor. This is the listener that makes letting go always
 *     mean something.
 *  3. **The client's own refusals.** Two drops are answered without asking the server
 *     (see `drag.ts`), and this is where that sentence is *drawn* — the controller is
 *     where it is decided, because three different components can resolve a drop.
 *     Deliberately not the action bar's notice: that corner is for what the **server**
 *     said, and mixing the two would make it impossible to tell an answer from a guess.
 */
export const DragLayer: Component = () => {
  const dragging = useDrag();

  const onMove = (event: PointerEvent) => {
    if (!dragging.drag() && !dragging.skillDrag()) return;
    dragging.moveTo({ x: event.clientX, y: event.clientY });
  };

  // A cell's own `pointerup` runs first and clears the drag, so by the time this
  // fires there is nothing left to resolve — which is exactly the guard. What reaches
  // here is a release over anything that is *not* a cell.
  const onUp = () => {
    // A skill released over nothing is a change of mind, not a mistake, so it cancels
    // silently — where an item released over nothing has a sentence, because dropping
    // gear on the floor and abandoning a drag look identical to the player.
    if (dragging.skillDrag()) dragging.dropSkill(null);
    if (!dragging.drag()) return;
    dragging.drop({ kind: 'elsewhere' });
  };

  // Escape cancels, because a drag is a modal gesture and Escape is the game's back
  // button. It does not go through `game/keyboard.ts`: that router would also close
  // whatever panel is open, and cancelling a drag must not.
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || (!dragging.drag() && !dragging.skillDrag())) return;
    event.stopPropagation();
    event.preventDefault();
    dragging.cancel();
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  // Capture, so it runs before the panel router sees the key.
  window.addEventListener('keydown', onKey, true);
  onCleanup(() => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('keydown', onKey, true);
  });

  return (
    <>
      <Show when={dragging.drag()}>
        {(d) => {
          const size = () => rectOf(d().item);
          return (
            // A portal, because the ghost has to cross every panel it might be dropped
            // on and `#hud-root` holds stacking contexts it would otherwise sit inside.
            <Portal>
              <div
                class={`drag-ghost drag-ghost--${rarityClass(d().item.rarity)}`}
                data-testid="drag-ghost"
                aria-hidden="true"
                // Four unitless numbers; the *unit* is `--cell` in CSS, which is the
                // same token the grid is drawn from. So the ghost matches its grid at
                // every scale step and no pixel arithmetic crosses into TypeScript —
                // the offset by the grabbed cell, which keeps the square the player
                // picked up under the pointer, is `calc()` in `hud.css`.
                style={{
                  '--x': String(d().x),
                  '--y': String(d().y),
                  '--gx': String(d().grab.col),
                  '--gy': String(d().grab.row),
                  '--w': String(size().width),
                  '--h': String(size().height),
                }}
              >
                <Icon
                  class="drag-ghost__glyph"
                  iconId={d().item.iconId}
                  glyph={d().item.glyph}
                />
              </div>
            </Portal>
          );
        }}
      </Show>

      {/* The skill ghost. A different shape from the item's on purpose: an item ghost is
          drawn at its own footprint in grid cells, because *where it will fit* is the
          question; a skill occupies exactly one slot wherever it goes, so its ghost is a
          name on a chip and the question is only which slot. */}
      <Show when={dragging.skillDrag()}>
        {(d) => (
          <Portal>
            <div
              class="drag-ghost drag-ghost--skill"
              data-testid="skill-drag-ghost"
              aria-hidden="true"
              style={{ '--x': String(d().x), '--y': String(d().y) }}
            >
              <span class="drag-ghost__name">{d().name}</span>
            </div>
          </Portal>
        )}
      </Show>

      {/* The message lives on the controller rather than here, because a drop can be
          resolved by a grid cell, a socket or the window listener above — three places,
          one sentence. It is replaced by the next drop rather than aged out on a timer:
          it is an answer to a gesture the player just made, and the next gesture is the
          only thing that makes it stale. */}
      <Show when={dragging.notice()}>
        {(text) => (
          <div class="drop-notice" data-testid="drop-notice" role="status">
            {text()}
          </div>
        )}
      </Show>
    </>
  );
};
